import {
  collection,
  doc,
  updateDoc,
  writeBatch,
  increment,
  serverTimestamp,
} from 'firebase/firestore';
import { nanoid } from 'nanoid';
import { db } from '@/lib/firebase';
import { participantConverter } from './converters';
import type { Participant, PlayerInfo } from './types';

export const MAX_PARTICIPANTS = 128;

export const participantsCol = (tournamentId: string) =>
  collection(db, 'tournaments', tournamentId, 'participants').withConverter(participantConverter);

export const participantDoc = (tournamentId: string, pid: string) =>
  doc(db, 'tournaments', tournamentId, 'participants', pid).withConverter(participantConverter);

function makeDisplayName(players: PlayerInfo[]): string {
  return players.map((p) => p.name).join(' / ');
}

export async function addParticipant(
  tournamentId: string,
  input: { players: PlayerInfo[] }
): Promise<string> {
  const id = nanoid(10);
  const participant: Participant = {
    id,
    displayName: makeDisplayName(input.players),
    players: input.players,
    status: 'ACTIVE',
  };
  const batch = writeBatch(db);
  batch.set(participantDoc(tournamentId, id), participant);
  batch.update(doc(db, 'tournaments', tournamentId), {
    participantCount: increment(1),
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
  return id;
}

export async function addParticipantsBulk(
  tournamentId: string,
  inputs: { players: PlayerInfo[] }[]
): Promise<void> {
  if (inputs.length === 0) return;
  const batch = writeBatch(db);
  inputs.forEach((input) => {
    const id = nanoid(10);
    const participant: Participant = {
      id,
      displayName: makeDisplayName(input.players),
      players: input.players,
      status: 'ACTIVE',
    };
    batch.set(participantDoc(tournamentId, id), participant);
  });
  batch.update(doc(db, 'tournaments', tournamentId), {
    participantCount: increment(inputs.length),
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
}

export async function updateParticipant(
  tournamentId: string,
  pid: string,
  patch: Partial<Pick<Participant, 'players' | 'status'>>
): Promise<void> {
  const updates: Record<string, unknown> = { ...patch };
  if (patch.players) {
    updates.displayName = makeDisplayName(patch.players);
  }
  await updateDoc(participantDoc(tournamentId, pid), updates);
}

export async function deleteParticipant(tournamentId: string, pid: string): Promise<void> {
  const batch = writeBatch(db);
  batch.delete(participantDoc(tournamentId, pid));
  batch.update(doc(db, 'tournaments', tournamentId), {
    participantCount: increment(-1),
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
}
